import { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { estimateTransitMixerMins, formatTravelTime, TRANSIT_MIXER_AVG_KMPH } from '../utils/transitMixerEta';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const OVERPASS_URLS = [
    'https://overpass-api.de/api/interpreter',
    'https://lz4.overpass-api.de/api/interpreter',
];

let leafletLoader = null;

function loadLeaflet() {
    if (window.L) {
        return Promise.resolve(window.L);
    }
    if (leafletLoader) {
        return leafletLoader;
    }
    leafletLoader = new Promise((resolve, reject) => {
        if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = LEAFLET_CSS;
            document.head.appendChild(link);
        }
        const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
        const onReady = () => {
            if (window.L) {
                resolve(window.L);
            } else {
                reject(new Error('Could not load map.'));
            }
        };
        if (existing) {
            if (window.L) {
                onReady();
            } else {
                existing.addEventListener('load', onReady, { once: true });
            }
            return;
        }
        const script = document.createElement('script');
        script.src = LEAFLET_JS;
        script.async = true;
        script.onload = onReady;
        script.onerror = () => reject(new Error('Could not load map.'));
        document.body.appendChild(script);
    });
    return leafletLoader;
}

function streetQuery(lat, lon, radius) {
    return (
        '[out:json][timeout:15];'
        + `way["highway"]["name"](around:${radius},${lat},${lon});`
        + 'out geom;'
    );
}

async function fetchNamedStreets(lat, lon, radius = 450) {
    for (const url of OVERPASS_URLS) {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
                body: new URLSearchParams({ data: streetQuery(lat, lon, radius) }),
            });
            if (!response.ok) {
                continue;
            }
            const data = await response.json();
            const streets = (data.elements || []).reduce((list, element) => {
                const name = ((element.tags && element.tags.name) || '').trim();
                const latlngs = (element.geometry || []).map((point) => [point.lat, point.lon]);
                if (!name || latlngs.length < 2) {
                    return list;
                }
                list.push({ name, latlngs, key: name.toLowerCase() });
                return list;
            }, []).slice(0, 80);
            if (streets.length) {
                return streets;
            }
        } catch (err) {
            continue;
        }
    }
    return [];
}

async function fetchDrivingRoute(plant, site) {
    if (!plant?.lat || !plant?.lon || !site?.lat || !site?.lon) {
        return null;
    }
    const url = (
        'https://router.project-osrm.org/route/v1/driving/'
        + `${plant.lon},${plant.lat};${site.lon},${site.lat}`
        + '?overview=full&geometries=geojson'
    );
    const response = await fetch(url);
    if (!response.ok) {
        return null;
    }
    const data = await response.json();
    const route = data?.routes?.[0];
    if (!route) {
        return null;
    }
    const coords = (route.geometry?.coordinates || []).map(([lon, lat]) => [lat, lon]);
    const distanceKm = Math.round((Number(route.distance) / 1000) * 100) / 100;
    return {
        latlngs: coords,
        distanceKm,
        // Transit mixer ETA — not OSRM car duration
        durationMins: estimateTransitMixerMins(distanceKm),
    };
}

const MAP_MIN_ZOOM = 11;
const MAP_MAX_ZOOM = 18;
const MAP_DEFAULT_ZOOM = 15;

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function midpoint(latlngs) {
    const index = Math.floor((latlngs.length || 1) / 2);
    return latlngs[index] || latlngs[0];
}

function EstimatorLocationMapModal({
    open,
    title = 'Select site location',
    plant,
    site,
    selectedLabel = '',
    mapsUrl = '',
    onClose,
    onSelect,
}) {
    const mapNodeRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef({ plant: null, site: null });
    const routeLayerRef = useRef(null);
    const [leaflet, setLeaflet] = useState(null);
    const [draft, setDraft] = useState(site || null);
    const [draftLabel, setDraftLabel] = useState(selectedLabel || '');
    const [routeInfo, setRouteInfo] = useState(null);
    const [routeLoading, setRouteLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        let cancelled = false;
        loadLeaflet()
            .then((L) => {
                if (!cancelled) {
                    setLeaflet(L);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (!open || !leaflet || !mapNodeRef.current) {
            return undefined;
        }
        const L = leaflet;
        const center = site || plant || { lat: 13.0827, lon: 80.2707 };
        const startZoom = site || plant ? MAP_DEFAULT_ZOOM : 13;
        const map = L.map(mapNodeRef.current, {
            minZoom: MAP_MIN_ZOOM,
            maxZoom: MAP_MAX_ZOOM,
        }).setView([center.lat, center.lon], startZoom);
        const tiles = L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap, &copy; CARTO',
            subdomains: 'abcd',
            minZoom: MAP_MIN_ZOOM,
            maxZoom: MAP_MAX_ZOOM,
            maxNativeZoom: 18,
        }).addTo(map);
        let usedOsmFallback = false;
        tiles.on('tileerror', () => {
            if (usedOsmFallback || !mapRef.current) {
                return;
            }
            usedOsmFallback = true;
            map.removeLayer(tiles);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; OpenStreetMap contributors',
                minZoom: MAP_MIN_ZOOM,
                maxZoom: MAP_MAX_ZOOM,
                maxNativeZoom: 18,
            }).addTo(map);
        });
        mapRef.current = map;
        const streetLayer = L.layerGroup().addTo(map);
        routeLayerRef.current = L.layerGroup().addTo(map);
        let streetRequest = 0;
        let namedStreets = [];

        const drawStreetLabels = (streets) => {
            streetLayer.clearLayers();
            namedStreets = streets || [];
            const used = new Set();
            namedStreets.forEach((street) => {
                L.polyline(street.latlngs, {
                    color: '#94a3b8',
                    weight: 2,
                    opacity: 0.2,
                    interactive: false,
                }).addTo(streetLayer);
                if (used.has(street.key)) {
                    return;
                }
                used.add(street.key);
                const point = midpoint(street.latlngs);
                if (!point) {
                    return;
                }
                L.marker(point, {
                    interactive: false,
                    keyboard: false,
                    icon: L.divIcon({
                        className: 'estimator-street-label-icon',
                        html: `<span>${escapeHtml(street.name)}</span>`,
                        iconSize: [0, 0],
                        iconAnchor: [0, 0],
                    }),
                }).addTo(streetLayer);
            });
        };

        const nearestStreetName = (lat, lon) => {
            let best = '';
            let bestDist = 0.00045;
            namedStreets.forEach((street) => {
                street.latlngs.forEach(([sLat, sLon]) => {
                    const dist = Math.abs(sLat - lat) + Math.abs(sLon - lon);
                    if (dist < bestDist) {
                        bestDist = dist;
                        best = street.name;
                    }
                });
            });
            return best;
        };

        const loadStreetLabels = () => {
            if (!mapRef.current) {
                return;
            }
            const current = map.getCenter();
            const requestId = ++streetRequest;
            fetchNamedStreets(current.lat, current.lng, 550)
                .then((streets) => {
                    if (requestId === streetRequest && mapRef.current) {
                        drawStreetLabels(streets);
                    }
                })
                .catch(() => {});
        };

        if (plant) {
            markersRef.current.plant = L.circleMarker([plant.lat, plant.lon], {
                radius: 9,
                color: '#1d4ed8',
                fillColor: '#2563eb',
                fillOpacity: 1,
            }).addTo(map).bindPopup('Plant');
        }

        const syncSiteMarker = (coord, label) => {
            if (!coord || !mapRef.current) {
                return;
            }
            if (markersRef.current.site) {
                markersRef.current.site.setLatLng([coord.lat, coord.lon]);
            } else {
                markersRef.current.site = L.marker([coord.lat, coord.lon], {
                    draggable: true,
                    title: 'Site',
                }).addTo(mapRef.current);
                markersRef.current.site.on('dragend', (event) => {
                    const pos = event.target.getLatLng();
                    const streetName = nearestStreetName(pos.lat, pos.lng);
                    setDraft({ lat: pos.lat, lon: pos.lng, display_name: streetName });
                    setDraftLabel(streetName || 'Map pin');
                });
            }
            markersRef.current.site.bindPopup(label || 'Site');
        };

        if (site) {
            syncSiteMarker(site, selectedLabel);
        }

        // Draw shortest driving route plant → site
        if (plant && site) {
            setRouteLoading(true);
            fetchDrivingRoute(plant, site)
                .then((route) => {
                    if (!mapRef.current || !routeLayerRef.current || !route?.latlngs?.length) {
                        setRouteInfo(null);
                        return;
                    }
                    routeLayerRef.current.clearLayers();
                    const line = L.polyline(route.latlngs, {
                        color: '#dc2626',
                        weight: 5,
                        opacity: 0.85,
                    }).addTo(routeLayerRef.current);
                    map.fitBounds(line.getBounds(), { padding: [40, 40] });
                    setRouteInfo(route);
                })
                .catch(() => setRouteInfo(null))
                .finally(() => setRouteLoading(false));
        } else {
            setRouteInfo(null);
        }

        map.on('click', (event) => {
            const streetName = nearestStreetName(event.latlng.lat, event.latlng.lng);
            const coord = { lat: event.latlng.lat, lon: event.latlng.lng, display_name: streetName };
            setDraft(coord);
            setDraftLabel(streetName || 'Map pin');
            syncSiteMarker(coord, streetName || 'Selected point');
            if (plant) {
                setRouteLoading(true);
                fetchDrivingRoute(plant, coord)
                    .then((route) => {
                        if (!mapRef.current || !routeLayerRef.current || !route?.latlngs?.length) {
                            setRouteInfo(null);
                            return;
                        }
                        routeLayerRef.current.clearLayers();
                        L.polyline(route.latlngs, {
                            color: '#dc2626',
                            weight: 5,
                            opacity: 0.85,
                        }).addTo(routeLayerRef.current);
                        setRouteInfo(route);
                    })
                    .catch(() => setRouteInfo(null))
                    .finally(() => setRouteLoading(false));
            }
        });
        const resizeTimer = window.setTimeout(() => map.invalidateSize(), 100);
        const streetTimer = window.setTimeout(loadStreetLabels, 250);
        let moveTimer = null;
        const onViewChange = () => {
            window.clearTimeout(moveTimer);
            moveTimer = window.setTimeout(loadStreetLabels, 450);
        };
        map.on('moveend', onViewChange);
        map.on('zoomend', onViewChange);

        return () => {
            window.clearTimeout(resizeTimer);
            window.clearTimeout(streetTimer);
            window.clearTimeout(moveTimer);
            map.remove();
            mapRef.current = null;
            markersRef.current = { plant: null, site: null };
            routeLayerRef.current = null;
            setRouteInfo(null);
        };
    }, [leaflet, open]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setDraft(site || null);
        setDraftLabel(selectedLabel || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) {
        return null;
    }

    return (
        <>
            <div className="estimator-map-overlay" onClick={onClose} />
            <div className="estimator-map-modal" role="dialog" aria-modal="true">
                <div className="estimator-map-modal-header">
                    <h2>{title}</h2>
                    <button type="button" className="estimator-map-modal-close" aria-label="Close" onClick={onClose}>×</button>
                </div>
                <div className="estimator-map-modal-body">
                    <p className="estimator-map-hint">
                        Blue marker = plant · Pin = site · Red line = shortest driving route inside the app.
                        Drag the pin to adjust, then Select. Use Google Maps only if you want an external view.
                    </p>
                    <div ref={mapNodeRef} className="estimator-map-canvas" />
                    {routeLoading ? (
                        <div className="estimator-map-route-info">Loading shortest route…</div>
                    ) : null}
                    {routeInfo ? (
                        <div className="estimator-map-route-info">
                            <strong>Shortest route (Transit Mixer):</strong>
                            {' '}
                            {routeInfo.distanceKm} km one way
                            {' · '}
                            ~{formatTravelTime(routeInfo.durationMins)}
                            {' '}
                            (@ ~{TRANSIT_MIXER_AVG_KMPH} km/h)
                            {' · '}
                            Up &amp; Down {Math.round(routeInfo.distanceKm * 2 * 100) / 100} km
                        </div>
                    ) : null}
                    {draftLabel ? (
                        <div className="estimator-map-selected">{draftLabel}</div>
                    ) : null}
                </div>
                <div className="estimator-map-modal-actions">
                    {mapsUrl ? (
                        <Button
                            type="button"
                            color="info"
                            outline
                            onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}
                        >
                            Open in Google Maps
                        </Button>
                    ) : null}
                    <Button type="button" color="secondary" outline onClick={onClose}>Cancel</Button>
                    <Button
                        type="button"
                        color="primary"
                        disabled={!draft}
                        onClick={() => onSelect({
                            lat: draft.lat,
                            lon: draft.lon,
                            site_location: draftLabel,
                            route_distance_km: routeInfo?.distanceKm,
                            route_duration_mins: routeInfo?.durationMins,
                        })}
                    >
                        Select location
                    </Button>
                </div>
            </div>
        </>
    );
}

export default EstimatorLocationMapModal;
